// Public read-only Tax-Savings Viewer (Client Portal Phase 2 — d).
//
// Route: /ts/:token  (defined in App.js, no auth required)
//
// Renders the same report sections the CPA sees on /ocr/:jobId/tax-report
// (hero with savings total, missed deductions, errors, YoY swings, next
// steps) but under the firm's brand (logo, name, primary color) and
// with no edit / re-run / save controls.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import {
  Sparkles, TrendingUp, AlertCircle, CheckCircle2, Loader2,
  FileText, DollarSign, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtUSD = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(n || 0);

export default function PublicTaxSavingsPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ackName, setAckName] = useState('');
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/tax-savings/${token}`);
        if (r.status === 410) {
          throw new Error('This share link has been revoked by your CPA.');
        }
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || 'Report not found.');
        }
        const d = await r.json();
        setData(d);
        // Preserve any pre-typed name across reloads (e.g. after ack).
        if (d.client_name && !ackName) setAckName(d.client_name);
      } catch (e) {
        setError(e.message || 'Could not load report.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const acknowledge = async () => {
    setAcking(true);
    try {
      const r = await fetch(`${API_URL}/api/public/tax-savings/${token}/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: ackName || null }),
      });
      if (!r.ok) throw new Error('Could not record acknowledgment.');
      const d = await r.json();
      setData((cur) => cur ? { ...cur, acknowledged_at: d.acknowledged_at } : cur);
      toast.success('Thanks — your CPA will see that you reviewed this.');
    } catch (e) {
      toast.error(e.message || 'Acknowledgment failed.');
    } finally {
      setAcking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full" data-testid="public-tax-savings-error">
          <CardContent className="p-8 text-center space-y-3">
            <AlertCircle className="w-10 h-10 text-rose-500 mx-auto" />
            <div className="text-lg font-semibold">Report unavailable</div>
            <p className="text-sm text-muted-foreground">{error}</p>
            <p className="text-xs text-muted-foreground pt-2">
              Need help? Reach out to your CPA directly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { report, firm, acknowledged_at, client_name } = data;
  const totalSavings = report?.estimated_total_savings_usd || 0;
  const brand = firm?.brand_color || '#10b981';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-tax-savings-page">
      {/* Branded header */}
      <header
        className="border-b bg-white"
        style={{ borderTopWidth: 4, borderTopColor: brand, borderTopStyle: 'solid' }}
        data-testid="public-ts-header"
      >
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          {firm.logo_url ? (
            <img
              src={firm.logo_url}
              alt={firm.name}
              className="w-12 h-12 rounded-lg object-cover"
              data-testid="public-ts-firm-logo"
            />
          ) : (
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-xl"
              style={{ background: brand }}
              data-testid="public-ts-firm-initial"
            >
              {(firm.name || '?')[0].toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 truncate" data-testid="public-ts-firm-name">
              {firm.name}
            </div>
            <div className="text-xs text-slate-500">Tax Savings Review</div>
          </div>
          <Badge variant="secondary" className="hidden sm:inline-flex">
            <ShieldCheck className="w-3 h-3 mr-1" />
            Read-only
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Hero */}
        <Card
          className="border-2 overflow-hidden"
          style={{ borderColor: `${brand}33`, background: `linear-gradient(135deg, ${brand}0d, ${brand}1a)` }}
          data-testid="public-ts-hero"
        >
          <CardContent className="p-8 text-center space-y-3">
            <div
              className="text-sm uppercase tracking-wide font-semibold"
              style={{ color: brand }}
            >
              {firm.name} found potential tax savings of
            </div>
            <div
              className="text-5xl sm:text-6xl font-bold"
              style={{ color: brand }}
              data-testid="public-ts-savings-total"
            >
              {fmtUSD(totalSavings)}
            </div>
            <p className="text-sm sm:text-base max-w-2xl mx-auto text-slate-700">
              {report?.headline}
            </p>
            {client_name && (
              <p className="text-xs text-slate-500 pt-1">
                Prepared for {client_name}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Missed deductions */}
        {report?.missed_deductions?.length > 0 && (
          <Card data-testid="public-ts-deductions">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" style={{ color: brand }} />
                Missed deductions ({report.missed_deductions.length})
              </CardTitle>
              <CardDescription>
                Each one is a real tax-code citation — review these with your CPA before you file.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.missed_deductions.map((d, i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg border bg-card"
                  data-testid={`public-ts-deduction-${i}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{d.name}</div>
                      <p className="text-sm text-muted-foreground mt-1">{d.description}</p>
                      {d.irs_reference && (
                        <div className="text-xs text-muted-foreground mt-2 font-mono">
                          📎 {d.irs_reference}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-2xl font-bold" style={{ color: brand }}>
                        {fmtUSD(d.estimated_savings_usd)}
                      </div>
                      <div className="text-xs text-muted-foreground">est. savings</div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Errors */}
        {report?.errors?.length > 0 && (
          <Card data-testid="public-ts-errors">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-rose-600" />
                Items to double-check ({report.errors.length})
              </CardTitle>
              <CardDescription>Issues your CPA flagged for review.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.errors.map((e, i) => (
                <Alert
                  key={i}
                  className="border-rose-200 bg-rose-50"
                  data-testid={`public-ts-error-${i}`}
                >
                  <AlertCircle className="w-4 h-4 text-rose-600" />
                  <AlertTitle>Line {e.line}: {e.issue}</AlertTitle>
                  <AlertDescription>{e.fix}</AlertDescription>
                </Alert>
              ))}
            </CardContent>
          </Card>
        )}

        {/* YoY swings */}
        {report?.yoy_swings?.length > 0 && (
          <Card data-testid="public-ts-yoy">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-600" />
                Year-over-year movements ({report.yoy_swings.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {report.yoy_swings.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg border"
                  data-testid={`public-ts-yoy-${i}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{s.metric}</div>
                    <div className="text-sm text-muted-foreground">{s.commentary}</div>
                  </div>
                  <Badge variant={s.delta_pct > 0 ? 'default' : 'secondary'} className="ml-2 shrink-0">
                    {s.delta_pct > 0 ? '+' : ''}{s.delta_pct?.toFixed?.(1) ?? s.delta_pct}%
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Next steps */}
        {report?.next_steps?.length > 0 && (
          <Card data-testid="public-ts-next-steps">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" style={{ color: brand }} />
                Recommended next steps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-2 text-sm list-decimal list-inside marker:font-semibold" style={{ color: 'inherit' }}>
                {report.next_steps.map((s, i) => (
                  <li key={i} data-testid={`public-ts-next-step-${i}`}>{s}</li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}

        {/* Acknowledgment */}
        <Card className="border-dashed" data-testid="public-ts-acknowledge-card">
          {acknowledged_at ? (
            <CardContent className="p-6 text-center space-y-1">
              <CheckCircle2 className="w-8 h-8 mx-auto" style={{ color: brand }} />
              <div className="font-semibold">You acknowledged this report</div>
              <div className="text-xs text-muted-foreground">
                {new Date(acknowledged_at).toLocaleString()}
              </div>
            </CardContent>
          ) : (
            <CardContent className="p-6 space-y-3">
              <div className="text-sm">
                Let your CPA know you&apos;ve reviewed this report. They&apos;ll get a ping
                so they can follow up with you on next steps.
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="Your name (optional)"
                  value={ackName}
                  onChange={(e) => setAckName(e.target.value)}
                  data-testid="public-ts-ack-name"
                />
                <Button
                  onClick={acknowledge}
                  disabled={acking}
                  style={{ background: brand, color: '#fff' }}
                  className="hover:brightness-110"
                  data-testid="public-ts-ack-btn"
                >
                  {acking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  I&apos;ve reviewed this
                </Button>
              </div>
              {firm.contact_email && (
                <div className="text-xs text-muted-foreground pt-1">
                  Have questions?{' '}
                  <a
                    href={`mailto:${firm.contact_email}`}
                    className="underline"
                    style={{ color: brand }}
                  >
                    Email {firm.name}
                  </a>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        <div className="text-xs text-center text-muted-foreground py-4 flex items-center justify-center gap-1.5">
          <FileText className="w-3 h-3" />
          AI-assisted review. Not legal or tax advice. Final filing decisions rest with you and your CPA.
        </div>
        <div className="text-xs text-center text-slate-400 pb-4">
          Powered by{' '}
          <Sparkles className="w-3 h-3 inline" style={{ color: brand }} />
          {' '}<span className="font-medium">CountBucks</span>
        </div>
      </main>
    </div>
  );
}
